import { TFunction } from "i18next";
import {
  AcvTcvValues,
  RevenueKPIs,
  RevenueMovementColumn,
  RevenueRetention
} from "@/entities/reporting/types";
import BreakdownTable, { BreakdownCell, BreakdownRow } from "./BreakdownTable";
import BreakdownUnavailable from "./BreakdownUnavailable";
import { formatMoney, formatPercent, gainTone, lossTone } from "./format";
import { KPICardPopover } from "./KPICard";
import SummaryTiles from "./SummaryTiles";

/** The recurring-revenue terms, interpolated into labels, for the unit the report's figures are in. */
// A type alias, not an interface, so it passes as i18next interpolation options.
export type RevenueUnit = {
  arr: string;
  carr: string;
};

/** ARR and CARR, or MRR and CMRR when the report's figures are monthly. */
export const revenueUnit = (t: TFunction, isArr = true): RevenueUnit => ({
  arr: t(isArr ? "kpisV2.arr" : "kpisV2.mrr"),
  carr: t(isArr ? "kpisV2.carr" : "kpisV2.cmrr")
});

const money = (value: number | undefined): BreakdownCell => ({
  value: formatMoney(value)
});
const gain = (value: number | undefined): BreakdownCell => ({
  value: formatMoney(value),
  tone: gainTone(value)
});
const loss = (value: number | undefined): BreakdownCell => ({
  value: formatMoney(value),
  tone: lossTone(value)
});

/** The movement fields every breakdown table shows; only the ARR and CARR columns carry new business. */
type MovementValues = Pick<
  RevenueMovementColumn,
  "expansion" | "upsell" | "crossSell" | "contraction" | "churn"
> & { new?: number };

/** The rows a revenue movement shares across tables: new, expansion with its split, contraction and churn. */
const movementRows = (
  t: TFunction,
  columns: MovementValues[],
  { includeNew }: { includeNew: boolean }
): BreakdownRow[] => {
  const rows: BreakdownRow[] = [];
  if (includeNew) {
    rows.push({
      key: "new",
      label: t("kpisV2.rows.new"),
      cells: columns.map((column) => gain(column.new))
    });
  }
  rows.push(
    {
      key: "expansion",
      label: t("kpisV2.rows.expansion"),
      cells: columns.map((column) => gain(column.expansion)),
      children: [
        {
          key: "upsell",
          label: t("kpisV2.rows.upsell"),
          cells: columns.map((column) => money(column.upsell))
        },
        {
          key: "crossSell",
          label: t("kpisV2.rows.crossSell"),
          cells: columns.map((column) => money(column.crossSell))
        }
      ]
    },
    {
      key: "contraction",
      label: t("kpisV2.rows.contraction"),
      cells: columns.map((column) => loss(column.contraction))
    },
    {
      key: "churn",
      label: t("kpisV2.rows.churn"),
      cells: columns.map((column) => loss(column.churn))
    }
  );
  return rows;
};

export const currentRecurringRevenuePopover = (
  t: TFunction,
  revenue: RevenueKPIs,
  fiscalYearStart: string,
  unit: RevenueUnit
): KPICardPopover => {
  const crr = revenue.currentRecurringRevenue;
  const breakdown = revenue.breakdown;
  return {
    title: t("kpisV2.currentRecurringRevenue"),
    widthClassName: "w-[500px]",
    content: (
      <>
        <SummaryTiles
          tiles={[
            {
              key: "currentArr",
              label: t("kpisV2.tiles.currentArr", unit),
              value: formatMoney(crr.arr)
            },
            {
              key: "contractedChange",
              label: t("kpisV2.tiles.contractedChange"),
              value: formatMoney(crr.contractedChange, { signed: true })
            },
            {
              key: "carrFyExit",
              label: t("kpisV2.tiles.carrFyExit", unit),
              value: formatMoney(crr.carrFyExit)
            }
          ]}
        />
        {breakdown ? (
          <BreakdownTable
            dataTest="kpi-crr-breakdown"
            header={t("kpisV2.columns.breakdown")}
            columns={[unit.arr, t("kpisV2.columns.carrFyExit", unit)]}
            rows={[
              {
                key: "opening",
                label: t("kpisV2.rows.opening", { date: fiscalYearStart }),
                cells: [money(breakdown.arr.opening), money(breakdown.carr.opening)]
              },
              ...movementRows(t, [breakdown.arr, breakdown.carr], { includeNew: true }),
              {
                key: "closing",
                label: t("kpisV2.rows.closing"),
                cells: [money(breakdown.arr.closing), money(breakdown.carr.closing)]
              }
            ]}
          />
        ) : (
          <BreakdownUnavailable />
        )}
      </>
    )
  };
};

export const netNewRevenuePopover = (
  t: TFunction,
  revenue: RevenueKPIs,
  unit: RevenueUnit
): KPICardPopover => {
  const netNew = revenue.netNewRevenue;
  const breakdown = revenue.breakdown;
  const signed = (value: number | undefined): BreakdownCell => ({
    value: formatMoney(value, { signed: true })
  });
  return {
    title: t("kpisV2.netNewRevenue"),
    widthClassName: "w-[480px]",
    content: (
      <>
        <SummaryTiles
          tiles={[
            {
              key: "netNewArr",
              label: t("kpisV2.tiles.netNewArr", unit),
              value: formatMoney(netNew.arr, { signed: true })
            },
            {
              key: "netNewCarr",
              label: t("kpisV2.tiles.netNewCarr", unit),
              value: formatMoney(netNew.carr, { signed: true })
            }
          ]}
        />
        {breakdown ? (
          <BreakdownTable
            dataTest="kpi-net-new-breakdown"
            header=""
            columns={[t("kpisV2.columns.netNewArr", unit), t("kpisV2.columns.netNewCarr", unit)]}
            rows={[
              ...movementRows(t, [breakdown.arr, breakdown.carr], { includeNew: true }),
              {
                key: "netNew",
                label: t("kpisV2.rows.netNew"),
                cells: [signed(breakdown.arr.netNew), signed(breakdown.carr.netNew)]
              }
            ]}
          />
        ) : (
          <BreakdownUnavailable />
        )}
      </>
    )
  };
};

export const retentionPopover = (
  t: TFunction,
  retention: RevenueRetention,
  fiscalYearStart: string,
  unit: RevenueUnit
): KPICardPopover => ({
  title: t("kpisV2.retentionBreakdown"),
  widthClassName: "w-[480px]",
  content: (
    <>
      <SummaryTiles
        tiles={[
          { key: "nrr", label: t("kpis.nrr"), value: formatPercent(retention.nrr) },
          { key: "grr", label: t("kpis.grr"), value: formatPercent(retention.grr) }
        ]}
      />
      <BreakdownTable
        dataTest="kpi-retention-breakdown"
        header={t("kpisV2.columns.renewalState")}
        columns={[unit.arr]}
        rows={[
          {
            key: "opening",
            label: t("kpisV2.rows.openingArr", { ...unit, date: fiscalYearStart }),
            cells: [money(retention.opening)]
          },
          ...movementRows(t, [retention], { includeNew: false }),
          { key: "closing", label: t("kpisV2.rows.closing"), cells: [money(retention.closing)] }
        ]}
      />
    </>
  )
});

const acvTcvCells = (values: AcvTcvValues): BreakdownCell[] => [
  money(values.acv),
  money(values.tcv)
];

export const newBookingsPopover = (t: TFunction, revenue: RevenueKPIs): KPICardPopover => {
  const bookings = revenue.newBookings;
  const columns = [t("kpisV2.acv"), t("kpisV2.tcv")];
  return {
    title: t("kpisV2.newBookings"),
    widthClassName: "w-[480px]",
    content: (
      <>
        <SummaryTiles
          tiles={[
            { key: "acv", label: t("kpisV2.acv"), value: formatMoney(bookings.acv) },
            { key: "tcv", label: t("kpisV2.tcv"), value: formatMoney(bookings.tcv) }
          ]}
        />
        <BreakdownTable
          dataTest="kpi-bookings-contract-type"
          header={t("kpisV2.columns.contractType")}
          columns={columns}
          rows={[
            {
              key: "newBusiness",
              label: t("kpisV2.rows.newBusiness"),
              cells: acvTcvCells(bookings.newBusiness)
            },
            {
              key: "expansion",
              label: t("kpisV2.rows.expansion"),
              cells: acvTcvCells(bookings.expansion),
              children: [
                { key: "upsell", label: t("kpisV2.rows.upsell"), cells: acvTcvCells(bookings.upsell) },
                {
                  key: "crossSell",
                  label: t("kpisV2.rows.crossSell"),
                  cells: acvTcvCells(bookings.crossSell)
                }
              ]
            }
          ]}
        />
        <BreakdownTable
          dataTest="kpi-bookings-revenue-type"
          header={t("kpisV2.columns.revenueType")}
          columns={columns}
          rows={[
            { key: "recurring", label: t("kpisV2.rows.recurring"), cells: acvTcvCells(bookings.recurring) },
            { key: "oneTime", label: t("kpisV2.rows.oneTime"), cells: acvTcvCells(bookings.oneTime) }
          ]}
        />
      </>
    )
  };
};
